/*
 * FreshTrends Guided Shopping — Configuration (unified schema)
 * ────────────────────────────────────────────────────────────
 * Source-of-truth: docs/guided-shopping-schema.md
 *
 * All categories use the same answer key namespace (form, ornament_type,
 * gauge, dimension, etc.) and the same shared predicates. One code path.
 *
 * Compound answers: an option can carry `writes: { key: value, ... }` to
 * populate multiple keys in a single click (e.g. picking "Diamond" sets
 * ornament_type='gemstone' AND stone_type='diamond'). Runtime support:
 * guided-shopping.js onAnswer() applies option.writes when present.
 *
 * Categories: nose, belly, ear, nipple, lip, eyebrow, septum, tongue, all
 * Trigger buttons on the site use these ids via data-gs-trigger="<id>".
 */
(function (win) {
  'use strict';

  // ═══════════════════════════════════════════════════════════════════
  //   SHARED HELPERS
  // ═══════════════════════════════════════════════════════════════════

  function arrayContainsCI(arr, v) {
    if (!Array.isArray(arr)) return false;
    v = String(v).toLowerCase();
    for (var i = 0; i < arr.length; i++) {
      if (String(arr[i]).toLowerCase() === v) return true;
    }
    return false;
  }

  // Normalize gauge to bare "NG" form (strips "(1.0mm)" and similar tails).
  function normalizeGauge(s) {
    var m = String(s || '').match(/(\d+)\s*G/i);
    return m ? m[1] + 'G' : '';
  }
  function normalizedGaugeMatch(gauges, v) {
    if (!Array.isArray(gauges)) return false;
    var target = normalizeGauge(v);
    if (!target) return false;
    for (var i = 0; i < gauges.length; i++) {
      if (normalizeGauge(gauges[i]) === target) return true;
    }
    return false;
  }

  // Normalize dimension (length or diameter) to bare fraction like "3/8".
  // Strips "in", '"', mm parentheticals, whitespace.
  function normalizeDim(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/\(.*?\)/g, '')
      .replace(/in\b/g, '')
      .replace(/["]/g, '')
      .replace(/\s+/g, '')
      .replace(/mm$/, '');
  }
  function normalizedDimensionMatch(pool, v) {
    if (!Array.isArray(pool)) return false;
    var target = normalizeDim(v);
    if (!target) return false;
    for (var i = 0; i < pool.length; i++) {
      var n = normalizeDim(pool[i]);
      if (n === target || n.indexOf(target) !== -1) return true;
    }
    return false;
  }

  // Gem-size bucket (tiny / standard / bling): parse the leading number
  // and match it against a numeric range. This avoids the substring bug
  // where "1.2mm" contained "2mm" and false-matched the standard bucket.
  //
  //   tiny     — 1.1mm to 1.8mm  (branded: 1.2–1.7mm; catches 1.2mm variants)
  //   standard — 1.9mm to 2.3mm  (branded: 2mm)
  //   bling    — 2.4mm and up    (branded: 2.5mm+)
  function gemSizeMatchesBucket(sizes, bucket) {
    if (!Array.isArray(sizes) || sizes.length === 0) return false;
    for (var i = 0; i < sizes.length; i++) {
      var m = String(sizes[i]).match(/(\d+(?:\.\d+)?)\s*mm/i);
      if (!m) continue;
      var n = parseFloat(m[1]);
      if (isNaN(n)) continue;
      if (bucket === 'tiny'     && n >= 1.1 && n <= 1.8) return true;
      if (bucket === 'standard' && n >= 1.9 && n <= 2.3) return true;
      if (bucket === 'bling'    && n >= 2.4) return true;
    }
    return false;
  }

  // ═══════════════════════════════════════════════════════════════════
  //   SHARED PREDICATES — one per universal answer key
  // ═══════════════════════════════════════════════════════════════════

  var sharedPredicates = {
    piercing_type: function () { return true; }, // routing gate, not a filter

    form: function (p, v) { return p.form === v; },

    dangle: function (p, v) {
      var has = !!p.has_dangle;
      if (v === 'dangle')     return has;
      if (v === 'non_dangle') return !has;
      return true;
    },

    // Belly-specific: three-way form split. Emitter tags each belly product
    // with belly_form ∈ {curved_barbell, floating, hoop, spiral, circular}.
    // Detection is title-based (see quiz-catalog-emitter.liquid). Spirals and
    // circulars are their own emitted categories but the shopper-facing form
    // question only exposes curved_barbell / floating / hoop today — spiral
    // and circular are edge cases (~9 products total) that don't match any
    // shopper choice, so they fall out of every filtered set. If we add
    // shopper-facing options for them later, extend this predicate.
    //
    // Retained belly_style alias below — makes the earlier commits' data
    // survive the rename without breaking floating filtering elsewhere.
    belly_form: function (p, v) {
      if (v === 'floating')       return p.belly_form === 'floating';
      if (v === 'curved_barbell') return p.belly_form === 'curved_barbell';
      if (v === 'hoop')           return p.belly_form === 'hoop';
      return true;
    },
    belly_style: function (p, v) {
      // Legacy alias — treat 'classic' as curved_barbell for anything still
      // using belly_style in stored state (localStorage from prior sessions).
      if (v === 'floating') return !!p.is_floating;
      if (v === 'classic')  return p.belly_form === 'curved_barbell';
      return true;
    },

    ornament_type: function (p, v) {
      // stone_types is every gemstone bucket this product's variants actually
      // offer (stone_type collapses to ONE bucket by priority, which hides a
      // product like "Reina Diamond or Gemstone Curved Barbell" from CZ
      // searches just because it also has a diamond variant — Ximena,
      // 2026-08-13). Fall back to the singular field only if stone_types is
      // somehow missing (shouldn't happen on a live-fetched catalog).
      var types = Array.isArray(p.stone_types) ? p.stone_types : [p.stone_type];
      if (v === 'gemstone') {
        return types.indexOf('diamond') !== -1 || types.indexOf('cz') !== -1 || types.indexOf('gemstone') !== -1;
      }
      if (v === 'charm') return types.indexOf('charm') !== -1;
      if (v === 'plain') return types.indexOf('plain') !== -1;
      return true;
    },

    stone_type: function (p, v) {
      var types = Array.isArray(p.stone_types) ? p.stone_types : [p.stone_type];
      // 'diamond_cz' = merged clear-stone bucket (Diamond OR CZ — they read the
      // same to a shopper). Other values match the catalog stone_type directly
      // ('gemstone' = colored precious/semi-precious). Still strict — never
      // widens beyond the chosen bucket.
      if (v === 'diamond_cz') return types.indexOf('diamond') !== -1 || types.indexOf('cz') !== -1;
      return types.indexOf(v) !== -1;
    },

    stone_size: function (p, v) { return gemSizeMatchesBucket(p.gem_sizes, v); },

    gauge:     function (p, v) { return normalizedGaugeMatch(p.gauges, v); },

    dimension: function (p, v) {
      var pool = [].concat(p.post_lengths || []).concat(p.hoop_diameters || []);
      // Soft length (Paul, 2026-08-10): a product with NO captured length data
      // must not be dropped by the length step — length metafields are still
      // being backfilled, so excluding on missing data hides real products.
      // Only exclude when the product HAS length data and none of it matches.
      if (pool.length === 0) return true;
      return normalizedDimensionMatch(pool, v);
    },

    retention: function (p, v) {
      // Normalize hyphens/spaces so "Straight Post", "straight-post", and
      // "straight post" all match each other. Same for "L Post" / "L-Post".
      if (!Array.isArray(p.styles)) return false;
      function norm(s) { return String(s).toLowerCase().replace(/[-\s]+/g, ' ').trim(); }
      var target = norm(v);
      for (var i = 0; i < p.styles.length; i++) {
        if (norm(p.styles[i]) === target) return true;
      }
      return false;
    },

    metal: function (p, v) {
      // Catalog emits values like "14K Rose Gold", "18K Yellow Gold" —
      // our option values are bare color names ("rose", "white",
      // "yellow", "platinum"). Match by substring so any variant of
      // that metal color qualifies.
      if (!Array.isArray(p.metals)) return false;
      var needle = String(v).toLowerCase();
      for (var i = 0; i < p.metals.length; i++) {
        if (String(p.metals[i]).toLowerCase().indexOf(needle) !== -1) return true;
      }
      return false;
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  //   makeCategory FACTORY
  // ═══════════════════════════════════════════════════════════════════

  function makeCategory(spec) {
    var predicates      = spec.predicates || sharedPredicates;
    var structuralKeys  = {};
    (spec.structural_keys || []).forEach(function (k) { structuralKeys[k] = 1; });

    function applyFilters(catalog, answers, keys) {
      var pool = catalog;
      for (var i = 0; i < keys.length; i++) {
        var k = keys[i], v = answers[k], pred = predicates[k];
        if (!pred) continue;
        pool = pool.filter(function (p) { return pred(p, v); });
      }
      return pool;
    }

    // Soft-fallback drop priority. Items later in the array are dropped
    // SOONER. Structural keys are never dropped. Ranking rationale:
    //   metal (gold color)  — most droppable. Rose vs yellow vs white
    //     is a pure aesthetic swap within the same 14K gold tier.
    //   retention / dimension / gauge — fit + style. Dropping these
    //     gives the customer a physically wrong product; drop last.
    // NOTES:
    //   - platinum: material tier, not a color — the drop loop skips
    //     dropping `metal` when the answered value is 'platinum'.
    //   - stone_size: NEVER dropped. Ximena's rule: "gemstone size and
    //     style is more important than the gold color." Showing a Tiny
    //     to someone who picked Bling reads as broken. If no matches
    //     exist at their size, the bestseller-fallback state handles it.
    //   - stone_type: NEVER dropped (Paul, 2026-08-10). Diamond vs CZ vs
    //     colored is the whole point of the question — showing CZ to
    //     someone who chose Diamond reads as broken and low-grade. If no
    //     diamond pieces exist in this pool, the bestseller-fallback
    //     state handles it rather than silently swapping the stone.
    var DROP_PRIORITY = ['gauge', 'dimension', 'retention', 'metal'];

    return {
      id:               spec.id,
      label:            spec.label,
      intro:            spec.intro,
      catalogUrl:       spec.catalog_url,
      catalog_url:      spec.catalog_url,
      deferCatalog:     !!spec.defer_catalog,
      initial:          spec.initial,
      structuralKeys:   structuralKeys,
      structural_keys:  spec.structural_keys || [],
      defaults:         spec.defaults || {},
      questions:        spec.questions,
      intro_helix:      spec.intro_helix,
      filter: function (catalog, answers, answerOrder) {
        var keys = (answerOrder || Object.keys(answers)).slice();
        var pool = applyFilters(catalog, answers, keys);
        var dropped = [];   // list of key names that got relaxed
        // Soft fallback: drop by DROP_PRIORITY (aesthetic first), skipping
        // structural keys (and the sticky stone_size / stone_type). Only pads
        // when the strict set is thin — 3 results or fewer (Paul, 2026-08-10):
        // a healthy result set is shown strictly, but a near-empty grid gets
        // aesthetic relaxations (gold color, gauge, length) to fill out. The
        // stone the customer chose is never relaxed, so this never turns a
        // Diamond search into CZ — it only widens color/fit within the stone.
        // Sticky-metal check: platinum is a material tier (real price
        // and material difference), not just a color pick, so we treat
        // it as structural for this run — the drop loop skips over it.
        var stickyMetal = String(answers.metal || '').toLowerCase() === 'platinum';
        while (pool.length < 4 && keys.length > 0) {
          var toDrop = null;
          for (var i = DROP_PRIORITY.length - 1; i >= 0; i--) {
            var k = DROP_PRIORITY[i];
            if (structuralKeys[k]) continue;
            if (k === 'metal' && stickyMetal) continue;
            var idx = keys.indexOf(k);
            if (idx !== -1) { toDrop = { name: k, idx: idx }; break; }
          }
          // Nothing left in priority list — fall back to dropping the most
          // recent non-structural answer (whatever it is). stone_size and
          // stone_type are sticky here too — the bestseller fallback beats
          // silently changing the stone the customer explicitly chose.
          if (!toDrop) {
            for (var j = keys.length - 1; j >= 0; j--) {
              if (structuralKeys[keys[j]]) continue;
              if (keys[j] === 'stone_size' || keys[j] === 'stone_type') continue;
              toDrop = { name: keys[j], idx: j }; break;
            }
          }
          if (!toDrop) break;
          dropped.push(toDrop.name);
          keys.splice(toDrop.idx, 1);
          pool = applyFilters(catalog, answers, keys);
        }
        // Stash the dropped list on the pool so the results renderer can
        // surface a "we relaxed X" banner. Non-standard but pragmatic.
        pool._droppedFilters = dropped;
        // Whether a product has a variant that matches BOTH the customer's
        // metal AND length answer exactly. When true, deep-linking to that
        // product opens the PDP with the exact combo pre-selected (no
        // partial-match fallback). Cheap enough to compute in the sort.
        // Length is normalized via normalizeDim so "5/16" matches "5/16in"
        // and "5/16in (8mm)" — the emitter emits the "in" suffix but the
        // customer's answer omits it.
        function hasExactVariantMatch(p) {
          var vs = (p && p.variants) || [];
          if (!vs.length) return false;
          var wantMetal  = String(answers.metal || '').toLowerCase();
          var wantLength = normalizeDim(answers.dimension || '');
          if (!wantMetal && !wantLength) return false;
          for (var i = 0; i < vs.length; i++) {
            var v = vs[i];
            var mOk = !wantMetal  || String(v.metal  || '').toLowerCase() === wantMetal;
            var lOk = !wantLength || normalizeDim(v.length || '') === wantLength;
            if (mOk && lOk) return true;
          }
          return false;
        }

        // Sort priority (each subsequent rule breaks ties from the previous):
        //   1. is_navel_specific — belly-specific products first. No-op on
        //      categories where the field is undefined (nose, ear, etc).
        //   2. hasExactVariantMatch — products whose variants can honor the
        //      shopper's exact metal+length combo. Fixes the "deep-linked
        //      variant has wrong length" case: generic curved barbells
        //      stocked in 3/8"/7/16" won't jump ahead of a navel ring
        //      that actually has 5/16".
        //   3. sales_rank when populated (lower = better)
        //   4. is_bestseller badge (true first)
        //   5. price DESC (premium-first as tiebreak)
        pool.sort(function (a, b) {
          if (!!a.is_navel_specific !== !!b.is_navel_specific) return a.is_navel_specific ? -1 : 1;
          var aExact = hasExactVariantMatch(a);
          var bExact = hasExactVariantMatch(b);
          if (aExact !== bExact) return aExact ? -1 : 1;
          var ar = (typeof a.sales_rank === 'number') ? a.sales_rank : null;
          var br = (typeof b.sales_rank === 'number') ? b.sales_rank : null;
          if (ar !== null && br !== null) return ar - br;
          if (ar !== null) return -1;
          if (br !== null) return 1;
          if (a.is_bestseller !== b.is_bestseller) return a.is_bestseller ? -1 : 1;
          return b.price_min - a.price_min;
        });
        return pool;
      },
      strictCount: function (catalog, hypo) {
        return applyFilters(catalog, hypo, Object.keys(hypo)).length;
      }
    };
  }

  // ═══════════════════════════════════════════════════════════════════
  //   SHARED HELIX INTRO
  // ═══════════════════════════════════════════════════════════════════

  var INTRO_HELIX = {
    title: "Hi, I'm Ruby 👋",
    body:  "I'm your FreshTrends piercing advisor. I'll walk you through a few quick questions to match you to the right piece — with the right fit and the right look."
  };

  // Q_cart_form is shared by 4 ear placements (helix/forward_helix/tragus/
  // anti_tragus) — these maps let its question/helix text name-check which
  // one the shopper actually picked instead of generic "Cartilage forms".
  var CART_PLACEMENT_LABEL = {
    helix: 'Helix', forward_helix: 'Forward helix', tragus: 'Tragus', anti_tragus: 'Anti-tragus'
  };
  var CART_PLACEMENT_WHERE = {
    helix: 'Helix piercings sit along the upper outer rim of the ear cartilage',
    forward_helix: 'Forward helix piercings sit at the front of the ear cartilage, near the temple',
    tragus: 'Tragus piercings sit on the small flap of cartilage just in front of the ear canal',
    anti_tragus: 'Anti-tragus piercings sit on the small cartilage bump opposite the tragus'
  };

  // ═══════════════════════════════════════════════════════════════════
  //   METAL QUESTION — asked last in every category before results.
  //   Filters the pool to products available in that metal AND makes the
  //   result card show the picked-color image / variant URL / price.
  // ═══════════════════════════════════════════════════════════════════

  // nextStep: where metal goes after it's answered (string id, or a function
  // of state.answers for categories whose branch determines the target —
  // e.g. ear's piercing_type, nose's stud/hoop form). Defaults to 'final'
  // for the rare category with nothing left to ask after metal.
  function metalQ(nextStep) {
    var next = (nextStep === undefined) ? 'final' : nextStep;
    return {
      key: 'metal', question: 'Pick your metal', cols: 4,
      helix: {
        title: 'Metal color',
        body: "Solid 14K gold in yellow, white, or rose. Platinum on select pieces. All metals are equally durable — this is purely an aesthetic pick. Results below will show your color."
      },
      options: [
        { value: 'yellow',   label: '14K Yellow Gold', next: next },
        { value: 'white',    label: '14K White Gold',  next: next },
        { value: 'rose',     label: '14K Rose Gold',   next: next },
        { value: 'platinum', label: 'Platinum',        next: next }
      ]
    };
  }

  // ═══════════════════════════════════════════════════════════════════
  //   NOSE RING
  // ═══════════════════════════════════════════════════════════════════

  var noseRings = makeCategory({
    id: 'nose',
    label: 'Nose Rings',
    intro: "Answer a few quick questions and we'll match you to your ideal nose ring.",
    catalog_url: '/collections/nose-rings?view=quiz-catalog',
    initial: 'Q_form',
    structural_keys: ['form', 'ornament_type'],
    defaults: { gauge: '20G', dimension: '5/16' },
    questions: {
      // Metal moves right after ornament/stone is chosen (Ximena, 2026-08-12)
      // — an aesthetic pick people already have an opinion on, ahead of the
      // technical gauge/retention/diameter questions people are more likely
      // to be unsure about. Target branches on 'form' since stud and hoop
      // reconverge at different next questions.
      Q_metal: metalQ(function (answers) {
        return (answers && answers.form === 'hoop') ? 'Q_hoop_gauge' : 'Q_stud_gauge';
      }),
      Q_form: {
        key: 'form', question: 'Pick your style', cols: 2,
        helix: {
          title: 'Studs vs hoops',
          body: "Studs sit close to your nostril like a tiny stud earring — minimal, polished, easy to forget you're wearing. Hoops curve through the piercing for a fuller, more visible look."
        },
        options: [
          { value: 'stud', label: 'Stud', img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/style-stud-icon.png', next: 'Q_stud_ornament' },
          { value: 'hoop', label: 'Hoop', img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/style-hoop-icon.png', next: 'Q_hoop_ornament' }
        ]
      },

      // ── STUD branch ─────────────────────────────────────────────────
      Q_stud_ornament: {
        key: 'ornament_type', question: 'Gemstone, charm, or plain?', cols: 3,
        helix: {
          title: 'Gemstone, charm, or plain',
          body: "A gemstone is a faceted sparkling stone — diamond, CZ, or colored gem. A charm is a small shaped ornament — star, moon, heart, flower — with no faceted stone. Plain is solid gold — a simple ball, dome, or disc, no stone."
        },
        options: [
          { value: 'gemstone', label: 'Gemstone',         img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/gemstone-icon.png?v=2', next: 'Q_stud_stone_type' },
          { value: 'charm',    label: 'Charm',            img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/charm-icon.png',        next: 'Q_stud_retention' },
          { value: 'plain',    label: 'Plain (no stone)', img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/plain-icon_copy.png',   img_photo: true, next: 'Q_stud_retention' }
        ]
      },
      Q_stud_stone_type: {
        key: 'stone_type', question: 'Pick your stone', cols: 3,
        helix: {
          title: 'Diamond, CZ, or colored',
          body: "Diamonds are real, lab-graded VS-quality. Cubic zirconia looks like a diamond and costs much less — a great starter option. Colored precious / semi-precious stones (ruby, sapphire, emerald, topaz) add personality."
        },
        options: [
          { value: 'diamond',  label: 'Diamond',            img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/diamond-round-icon-opt.webp', img_photo: true, next: 'Q_stud_stone_size' },
          { value: 'cz',       label: 'Cubic Zirconia',     img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/cz-icon_copy.png',        img_photo: true, next: 'Q_stud_stone_size' },
          { value: 'gemstone', label: 'Precious gem',       img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/gemstone-icon_copy.png',  img_photo: true, next: 'Q_stud_stone_size' }
        ]
      },
      Q_stud_stone_size: {
        key: 'stone_size', question: 'Pick your size', cols: 3,
        helix: {
          title: 'Stone size',
          body: "Stone diameter affects how visible the piece is on the nose. 1.5–1.7mm reads as a subtle accent — visible up close but not from across a room. 2mm catches light readily and is what most piercers fit as a default. 2.5mm and up is easy to spot from a few feet away."
        },
        options: [
          { value: 'tiny',     label: 'Tiny',     desc: '1.2–1.7mm', next: 'Q_stud_retention' },
          { value: 'standard', label: 'Standard', desc: '2mm',       next: 'Q_stud_retention' },
          { value: 'bling',    label: 'Bling',    desc: '2.5mm+',    next: 'Q_stud_retention' }
        ]
      },
      Q_stud_retention: {
        key: 'retention', question: 'How it stays in', cols: 5,
        helix: {
          title: 'Retention style',
          body: "<b>Flat-back</b> sits flush against the inside of your nostril — most comfortable for daily wear. <b>L-post</b> is straight then bent at an L — easy to insert, stays put. <b>Bone</b> is a straight post with a small bump at the tip that anchors it. <b>Straight post</b> is a plain smooth post that can be bent to fit, by you or a professional piercer. <b>Twist</b> screws in like a corkscrew — traditional, secure, customer favorite."
        },
        options: [
          { value: 'twist',         label: 'Twist',         img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/backing-icon-twist.png',         next: 'Q_metal' },
          { value: 'bone',          label: 'Bone',          img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/backing-icon-bone.png',          next: 'Q_metal' },
          { value: 'l-post',        label: 'L-post',        img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/backing-icon-L-post.png',        next: 'Q_metal' },
          { value: 'straight-post', label: 'Straight post', img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/backing-icon-straight-post.png', next: 'Q_metal' },
          { value: 'flatback',      label: 'Flat-back',     img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/backing-icon-flatback.png',      next: 'Q_metal' }
        ]
      },
      Q_stud_gauge: {
        key: 'gauge', question: 'Pick your gauge', cols: 2,
        helix: {
          title: 'Nose gauge',
          body: "Gauge is the thickness of the post that goes through your piercing. Most US nose piercings are done at 20G (0.8mm) — check what you currently wear if you can. 18G (1.0mm) is one step thicker. Going up a gauge stretches the piercing permanently, so match your current size unless a piercer has confirmed otherwise."
        },
        options: [
          { value: '20G', label: '20G', desc: '0.8mm — standard', next: 'final' },
          { value: '18G', label: '18G', desc: '1.0mm — thicker',  next: 'final' }
        ]
      },

      // ── HOOP branch ─────────────────────────────────────────────────
      Q_hoop_ornament: {
        key: 'ornament_type', question: 'Gemstone or plain?', cols: 2,
        helix: {
          title: 'Stone or plain',
          body: "A plain hoop is a clean continuous circle of solid gold — timeless and minimal. A gemstone hoop has a small accent stone set into the ring for a hint of sparkle."
        },
        options: [
          { value: 'gemstone', label: 'Gemstone', img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/gemstone-icon.png?v=2', next: 'Q_hoop_stone_type' },
          { value: 'plain',    label: 'Plain',    img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/style-hoop-icon.png', next: 'Q_metal'      }
        ]
      },
      Q_hoop_stone_type: {
        key: 'stone_type', question: 'Pick your stone', cols: 3,
        helix: {
          title: 'Diamond, CZ, or colored',
          body: "Same options as studs — diamonds (real, VS-graded), cubic zirconia (looks like a diamond, costs less), or precious / semi-precious (ruby, sapphire, emerald)."
        },
        options: [
          { value: 'diamond',  label: 'Diamond',      img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/diamond-round-icon-opt.webp', img_photo: true, next: 'Q_metal' },
          { value: 'cz',       label: 'Cubic Zirconia', img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/cz-icon_copy.png',        img_photo: true, next: 'Q_metal' },
          { value: 'gemstone', label: 'Precious gem', img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/gemstone-icon_copy.png',  img_photo: true, next: 'Q_metal' }
        ]
      },
      Q_hoop_gauge: {
        key: 'gauge', question: 'Pick your gauge', cols: 2,
        helix: {
          title: 'Hoop gauge',
          body: "Gauge is the thickness of the wire. 20G (0.8mm) is the standard US nose-piercing gauge. 18G (1.0mm) is one step thicker. Match your current piercing — going up a gauge requires stretching."
        },
        options: [
          { value: '20G', label: '20G', desc: '0.8mm — standard', next: 'Q_hoop_dimension' },
          { value: '18G', label: '18G', desc: '1.0mm — thicker',  next: 'Q_hoop_dimension' }
        ]
      },
      Q_hoop_dimension: {
        key: 'dimension', question: 'Pick your diameter', cols: 3,
        helix: {
          title: 'Hoop diameter',
          body: "Small (1/4in / 6mm) hugs the nostril closely. Standard (5/16in / 8mm) is the most common — visible but not oversized. Large (3/8in / 10mm) is a fuller statement hoop."
        },
        options: [
          { value: '1/4',  label: 'Small',    desc: '1/4" (6mm)',  next: 'final' },
          { value: '5/16', label: 'Standard', desc: '5/16" (8mm)', next: 'final' },
          { value: '3/8',  label: 'Large',    desc: '3/8" (10mm)', next: 'final' }
        ]
      }
    },
    intro_helix: INTRO_HELIX
  });

  // ═══════════════════════════════════════════════════════════════════
  //   BELLY BUTTON — curved barbell only (no hoops in real usage)
  // ═══════════════════════════════════════════════════════════════════

  var bellyRings = makeCategory({
    id: 'belly',
    label: 'Belly Button Rings',
    intro: "A few quick questions and we'll find your perfect navel ring.",
    catalog_url: '/collections/belly-button-rings?view=quiz-catalog',
    initial: 'Q_belly_form',
    // form is asked upfront — the belly-button-rings collection isn't just
    // J-curve barbells; it includes floating navels + hoops (seam rings,
    // captive bead, clickers). Emitter categorizes each product via
    // belly_form. Skipping this question was giving hoops in "classic
    // curved barbell" results — the 2-way split was wrong.
    //
    // Gauge is NOT asked: 99% of navel piercings are 14G, so asking creates
    // more false negatives than good matches. Filter shows all gauges;
    // shopper can eyeball on the PDP if they run 16G.
    //
    // dimension is structural for curved+floating so live-hiding fires on
    // Q_belly_dimension: floating navels use 3/16–5/16" (5–8mm) while
    // classic curved barbells use 1/4–1/2" (6–13mm). Both length sets live
    // in the same question and refreshOptions hides whichever set doesn't
    // match belly_form. Hoops skip dimension entirely (their sizing is
    // diameter, a different concept — deferred to a future iteration).
    structural_keys: ['belly_form', 'ornament_type', 'dimension'],
    questions: {
      // Metal moves right after ornament (Ximena, 2026-08-12) — hoops skip
      // straight to results (they have no length step); curved/floating go
      // on to length.
      Q_metal: metalQ(function (answers) {
        return (answers && answers.belly_form === 'hoop') ? 'final' : 'Q_belly_dimension';
      }),
      Q_belly_form: {
        key: 'belly_form', question: 'What style of navel ring?', cols: 3,
        helix: {
          title: 'Curved barbell, floating, or hoop',
          body: "A <b>curved barbell</b> is the traditional J-shaped navel piece — top stone or ornament, bottom charm sits outside the piercing. A <b>floating</b> navel ring has a flush top and a discreet disc on the bottom, sitting snug to the piercing. A <b>hoop</b> is a ring worn through the piercing — seam rings, captive bead rings, or clickers — for a more open look, best on well-healed piercings."
        },
        options: [
          { value: 'curved_barbell', label: 'Curved barbell', img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/curved-belly-ring-icon.png', next: 'Q_belly_ornament' },
          { value: 'floating',       label: 'Floating',       img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/floating-navel-icon.png',   next: 'Q_belly_ornament' },
          { value: 'hoop',           label: 'Hoop',           img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/style-hoop-icon.png',       next: 'Q_belly_ornament' }
        ]
      },
      Q_belly_ornament: {
        key: 'ornament_type', question: 'Stone type', cols: 4,
        helix: {
          title: 'Stone or plain',
          body: "Diamond, CZ, and colored gemstones are your sparkle options. There are also plain-ball and plain-disc curved barbells — clean solid gold, no stone. Great for everyday, healing periods, or a minimalist look."
        },
        // Metal now always comes right after ornament (Ximena, 2026-08-12) —
        // Q_metal itself branches hoop vs curved/floating from here.
        options: [
          { value: 'diamond',  label: 'Diamond',           img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/diamond-round-icon-opt.webp', img_photo: true, writes: { ornament_type: 'gemstone', stone_type: 'diamond'  }, next: 'Q_metal' },
          { value: 'cz',       label: 'Cubic Zirconia',    img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/cz-icon_copy.png',        img_photo: true, writes: { ornament_type: 'gemstone', stone_type: 'cz'       }, next: 'Q_metal' },
          { value: 'precious', label: 'Colored gem',       img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/gemstone-icon_copy.png',  img_photo: true, writes: { ornament_type: 'gemstone', stone_type: 'gemstone' }, next: 'Q_metal' },
          { value: 'plain',    label: 'Plain (no stone)',  img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/plain-icon_copy.png',     img_photo: true, writes: { ornament_type: 'plain'    },                        next: 'Q_metal' }
        ]
      },
      Q_belly_dimension: {
        key: 'dimension', question: 'Pick your length', cols: 3,
        helix: {
          title: 'Barbell length',
          body: "Length is the critical fit question — <b>match what you currently wear</b>. <b>Floating</b> navels sit snug to the piercing: the focal design sits on top of the piercing with a discreet, comfortable disc on the bottom, so the decorative piece isn't hidden inside the belly. Range: 3/16\"–5/16\" (5–8mm). <b>Classic curved</b> barbells need more room for the top stone and bottom charm to sit outside the piercing, so the range is wider — 1/4\"–1/2\" (6–13mm) — with 3/8\" the most common."
        },
        // Options depend on belly_form: floating gets 3 shorter lengths;
        // curved_barbell gets 5 lengths from Extra Short through Extra Long.
        // Hoops never reach this step — Q_belly_ornament routes them to
        // Q_metal directly (they use diameter, not length).
        // Runtime supports options-as-function (guided-shopping.js).
        options: function (answers) {
          if (answers && answers.belly_form === 'floating') {
            return [
              { value: '3/16', label: 'Short',    desc: '3/16" (5mm)', next: 'final' },
              { value: '1/4',  label: 'Standard', desc: '1/4" (6mm)',  next: 'final' },
              { value: '5/16', label: 'Long',     desc: '5/16" (8mm)', next: 'final' }
            ];
          }
          return [
            { value: '1/4',  label: 'Extra short', desc: '1/4" (6mm)',   next: 'final' },
            { value: '5/16', label: 'Short',       desc: '5/16" (8mm)',  next: 'final' },
            { value: '3/8',  label: 'Standard',    desc: '3/8" (10mm)',  next: 'final' },
            { value: '7/16', label: 'Long',        desc: '7/16" (11mm)', next: 'final' },
            { value: '1/2',  label: 'Extra long',  desc: '1/2" (13mm)',  next: 'final' }
          ];
        }
      }
    },
    intro_helix: INTRO_HELIX
  });

  // ═══════════════════════════════════════════════════════════════════
  //   EAR — 10 sub-piercings, routed via piercing_type
  //
  //   Each piercing has its own form/gauge/dimension defaults. We route
  //   first, then run a piercing-specific mini-tree. Router uses
  //   categorySwitch pattern: not a filter, just picks the sub-tree.
  //
  //   For brevity we group similar sub-piercings behind shared questions
  //   (helix / forward helix / tragus / anti-tragus all go to the same
  //   cartilage-stud mini-tree).
  // ═══════════════════════════════════════════════════════════════════

  var earPiercings = makeCategory({
    id: 'ear',
    label: 'Ear Piercings',
    intro: "Which piercing are you shopping for? Different piercings need different jewelry.",
    // Each placement option (Q_ear_piercing below) carries its own catalog_url
    // pointing at that piercing's curated collection, so the result pool is
    // "the Tragus collection", "the Conch collection", etc. — not one generic
    // earrings pool. This catalog_url is only the fallback (full ear catalog)
    // used for the "browse the full collection" CTA; defer_catalog stops it
    // auto-loading at quiz start so it can't race the placement swap.
    catalog_url: '/collections/ear-piercings?view=quiz-catalog',
    defer_catalog: true,
    initial: 'Q_ear_piercing',
    structural_keys: ['piercing_type', 'form', 'ornament_type'],
    defaults: { gauge: '16G', dimension: '5/16' },
    questions: {
      // Metal moves right after ornament/stone (Ximena, 2026-08-12) — target
      // branches on piercing_type since each ear sub-flow reconverges at a
      // different gauge/dimension question (or straight to results for lobe,
      // which has no gauge question at all).
      Q_metal: metalQ(function (answers) {
        var pt = answers && answers.piercing_type;
        if (pt === 'lobe') return 'final';
        if (pt === 'conch') return 'Q_conch_gauge';
        if (pt === 'rook' || pt === 'daith' || pt === 'snug') return 'Q_curved_gauge';
        if (pt === 'industrial') return 'Q_ind_dimension';
        return 'Q_cart_gauge'; // helix, forward_helix, tragus, anti_tragus
      }),
      Q_ear_piercing: {
        key: 'piercing_type', question: 'Which piercing?', cols: 4,
        helix: {
          title: 'Which piercing?',
          body: "Different ear piercings need different jewelry shapes. Pick yours and I'll narrow the options accordingly."
        },
        options: [
          // Each placement's catalog_url is its own curated collection — this is
          // what makes "pick Tragus" actually search the Tragus collection.
          // Placements without a dedicated collection fall back to the general
          // Cartilage collection (helix / anti-tragus / snug are all cartilage).
          { value: 'lobe',           label: 'Lobe',            img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/lobe-icon-opt.png',          img_photo: true, catalog_url: '/collections/earrings?view=quiz-catalog',                    next: 'Q_lobe_form'          },
          { value: 'helix',          label: 'Helix',           img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/helix-icon-opt.png',         img_photo: true, catalog_url: '/collections/cartilage-earrings?view=quiz-catalog',          next: 'Q_cart_form'          },
          { value: 'forward_helix',  label: 'Forward helix',   img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/forward-helix-icon-opt.png', img_photo: true, catalog_url: '/collections/forward-helix-earrings?view=quiz-catalog',      next: 'Q_cart_form'          },
          { value: 'tragus',         label: 'Tragus',          img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/tragus-icon-opt.png',        img_photo: true, catalog_url: '/collections/tragus-body-jewelry?view=quiz-catalog',         next: 'Q_cart_form'          },
          { value: 'anti_tragus',    label: 'Anti-tragus',     img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/antitragus-tragus-icon-opt-v2.png', img_photo: true, catalog_url: '/collections/cartilage-earrings?view=quiz-catalog',          next: 'Q_cart_form'          },
          { value: 'conch',          label: 'Conch',           img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/conch-icon-opt.png',         img_photo: true, catalog_url: '/collections/conch?view=quiz-catalog',                       next: 'Q_conch_form'         },
          { value: 'rook',           label: 'Rook',            img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/rook-icon-opt.png',          img_photo: true, catalog_url: '/collections/rook-piercing-jewelry?view=quiz-catalog',       next: 'Q_curved_form'        },
          { value: 'daith',          label: 'Daith',           img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/daith-icon-opt.png',         img_photo: true, catalog_url: '/collections/daith-piercing-jewelry?view=quiz-catalog',      next: 'Q_curved_form'        },
          { value: 'snug',           label: 'Snug',            img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/snug-icon-opt.png',          img_photo: true, catalog_url: '/collections/cartilage-earrings?view=quiz-catalog',          next: 'Q_curved_form'        },
          { value: 'industrial',     label: 'Industrial',      img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/industrial-icon-opt.png',    img_photo: true, catalog_url: '/collections/industrial-piercing-jewelry?view=quiz-catalog', next: 'Q_ind_ornament'       }
        ]
      },

      // ── LOBE branch ─────────────────────────────────────────────────
      Q_lobe_form: {
        key: 'form', question: 'Stud, hoop, or dangle?', cols: 3,
        helix: {
          title: 'Lobe forms',
          body: "Lobe piercings can wear traditional stud earrings, hoops, or dangle designs. Pick the shape you want."
        },
        options: [
          { value: 'stud',   label: 'Stud',   img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/style-stud-icon.png', next: 'Q_lobe_ornament' },
          { value: 'hoop',   label: 'Hoop',   img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/style-hoop-icon.png', next: 'Q_lobe_ornament' },
          // Dangle skips straight to Metal, no ornament_type/stone_type step
          // (Ximena, 2026-08-12) — inventory is only 2-3 products, all
          // diamond, so there's nothing real to choose. `dangle: 'dangle'`
          // uses the existing dangle predicate (was previously written as
          // the dead key has_dangle_want, which no predicate ever read —
          // Dangle silently matched the same pool as Stud). Not in
          // DROP_PRIORITY, so it's sticky like stone_size/stone_type: a
          // shopper who picks Dangle never gets soft-fallbacked into a
          // regular stud.
          { value: 'dangle', label: 'Dangle', img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/dangle-earrings-icon.png', writes: { form: 'stud', dangle: 'dangle' }, next: 'Q_metal' }
        ]
      },
      // Traditional lobe piercings are standard earrings — practically always
      // 20G, so there's nothing real to ask. Charm/Plain/gemstone-drilldown
      // options write gauge:'20G' directly and skip straight to Q_metal
      // instead of routing through a dedicated gauge question.
      Q_lobe_ornament: {
        key: 'ornament_type', question: 'Stone type', cols: 3,
        helix: {
          title: 'Gemstone, charm, or plain',
          body: "A gemstone is a faceted sparkling stone — diamond, CZ, or colored gem. A charm is a small shaped ornament — star, moon, heart, flower — with no faceted stone. Plain is solid gold, no ornament."
        },
        options: [
          { value: 'gemstone', label: 'Gemstone',          img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/gemstone-icon.png?v=2', next: 'Q_lobe_stone_type' },
          { value: 'charm',    label: 'Charm',              img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/charm-icon.png',        writes: { ornament_type: 'charm', gauge: '20G' }, next: 'Q_metal' },
          { value: 'plain',    label: 'Plain (no stone)',  img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/plain-icon_copy.png',   img_photo: true, writes: { ornament_type: 'plain', gauge: '20G' }, next: 'Q_metal' }
        ]
      },
      Q_lobe_stone_type: {
        key: 'stone_type', question: 'Pick your stone', cols: 3,
        helix: {
          title: 'Diamond, CZ, or colored',
          body: "Diamonds are real, lab-graded VS-quality. Cubic zirconia looks like a diamond and costs much less. Colored precious / semi-precious stones add personality."
        },
        options: [
          { value: 'diamond',  label: 'Diamond',        img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/diamond-round-icon-opt.webp', img_photo: true, writes: { ornament_type: 'gemstone', stone_type: 'diamond', gauge: '20G'  }, next: 'Q_metal' },
          { value: 'cz',       label: 'Cubic Zirconia',  img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/cz-icon_copy.png',        img_photo: true, writes: { ornament_type: 'gemstone', stone_type: 'cz', gauge: '20G'       }, next: 'Q_metal' },
          { value: 'precious', label: 'Colored gem',     img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/gemstone-icon_copy.png',  img_photo: true, writes: { ornament_type: 'gemstone', stone_type: 'gemstone', gauge: '20G' }, next: 'Q_metal' }
        ]
      },

      // ── CARTILAGE STUD branch (helix / forward helix / tragus / anti-tragus)
      // Shared by 4 placements — question/helix text is a function of
      // state.answers.piercing_type so each one gets named specifically
      // instead of generic "Cartilage forms" copy (Ximena, 2026-08-12: "Ruby
      // should mention the actual piercing" when she landed on Forward Helix
      // and saw copy that never named it).
      Q_cart_form: {
        key: 'form',
        question: function (a) {
          var label = CART_PLACEMENT_LABEL[a.piercing_type] || 'This piercing';
          return label + ': flat-back stud or hoop?';
        },
        cols: 2,
        helix: {
          title: function (a) { return CART_PLACEMENT_LABEL[a.piercing_type] || 'Cartilage forms'; },
          body: function (a) {
            var where = CART_PLACEMENT_WHERE[a.piercing_type] || 'This piercing sits on the ear cartilage';
            return where + ". A flat-back stud sits flush against the back of the ear — most comfortable for cartilage. A hoop (small clicker or seamless ring) wraps through the piercing for a curved look.";
          }
        },
        options: [
          { value: 'stud', label: 'Flat-back stud', img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/backing-icon-flatback.png', writes: { form: 'stud' }, next: 'Q_cart_ornament' },
          { value: 'hoop', label: 'Hoop',           img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/style-hoop-icon.png',                                                    next: 'Q_cart_ornament' }
        ]
      },
      Q_cart_ornament: {
        key: 'ornament_type', question: 'Stone type', cols: 3,
        helix: {
          title: 'Gemstone, charm, or plain',
          body: "A gemstone is a faceted sparkling stone — diamond, CZ, or colored gem. A charm is a small shaped ornament — star, moon, heart, flower — with no faceted stone. Plain is solid gold, no ornament."
        },
        options: [
          { value: 'gemstone', label: 'Gemstone',          img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/gemstone-icon.png?v=2', next: 'Q_cart_stone_type' },
          { value: 'charm',    label: 'Charm',              img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/charm-icon.png',        next: 'Q_metal' },
          { value: 'plain',    label: 'Plain (no stone)',  img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/plain-icon_copy.png',   img_photo: true, next: 'Q_metal' }
        ]
      },
      Q_cart_stone_type: {
        key: 'stone_type', question: 'Pick your stone', cols: 3,
        helix: {
          title: 'Diamond, CZ, or colored',
          body: "Diamonds are real, lab-graded VS-quality. Cubic zirconia looks like a diamond and costs much less. Colored precious / semi-precious stones add personality."
        },
        options: [
          { value: 'diamond',  label: 'Diamond',        img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/diamond-round-icon-opt.webp', img_photo: true, writes: { ornament_type: 'gemstone', stone_type: 'diamond'  }, next: 'Q_metal' },
          { value: 'cz',       label: 'Cubic Zirconia',  img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/cz-icon_copy.png',        img_photo: true, writes: { ornament_type: 'gemstone', stone_type: 'cz'       }, next: 'Q_metal' },
          { value: 'precious', label: 'Colored gem',     img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/gemstone-icon_copy.png',  img_photo: true, writes: { ornament_type: 'gemstone', stone_type: 'gemstone' }, next: 'Q_metal' }
        ]
      },
      Q_cart_gauge: {
        key: 'gauge', question: 'Pick your gauge', cols: 2,
        helix: {
          title: 'Cartilage gauge',
          body: "Most cartilage piercings are done at 16G (1.2mm). Some helix and forward helix piercings are done at 18G (1.0mm). Match your current piercing."
        },
        options: [
          { value: '18G', label: '18G', desc: '1.0mm — slimmer',  next: 'Q_cart_dimension' },
          { value: '16G', label: '16G', desc: '1.2mm — standard', next: 'Q_cart_dimension' }
        ]
      },
      Q_cart_dimension: {
        key: 'dimension', question: 'Pick your size', cols: 3,
        helix: {
          title: 'Length or diameter',
          body: "For studs, this is the post length. For hoops, this is the diameter. Match what you currently wear if you can."
        },
        options: [
          { value: '1/4',  label: '1/4"',  desc: '(6mm)',  next: 'final' },
          { value: '5/16', label: '5/16"', desc: '(8mm)',  next: 'final' },
          { value: '3/8',  label: '3/8"',  desc: '(10mm)', next: 'final' }
        ]
      },

      // ── CONCH branch ────────────────────────────────────────────────
      Q_conch_form: {
        key: 'form', question: 'Larger stud or hoop?', cols: 2,
        helix: {
          title: 'Conch forms',
          body: "Conch piercings can carry a larger decorative flat-back stud (the ornament sits in the middle of the ear) or a captive ring / clicker."
        },
        options: [
          { value: 'stud', label: 'Flat-back stud', img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/backing-icon-flatback.png', writes: { form: 'stud' }, next: 'Q_conch_ornament' },
          { value: 'hoop', label: 'Hoop',           img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/style-hoop-icon.png',                                                    next: 'Q_conch_ornament' }
        ]
      },
      Q_conch_ornament: {
        key: 'ornament_type', question: 'Stone type', cols: 3,
        helix: {
          title: 'Gemstone, charm, or plain',
          body: "A gemstone is a faceted sparkling stone — diamond, CZ, or colored gem. A charm is a small shaped ornament — star, moon, heart, flower — with no faceted stone. Plain is solid gold, no ornament."
        },
        options: [
          { value: 'gemstone', label: 'Gemstone',          img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/gemstone-icon.png?v=2', next: 'Q_conch_stone_type' },
          { value: 'charm',    label: 'Charm',              img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/charm-icon.png',        next: 'Q_metal' },
          { value: 'plain',    label: 'Plain (no stone)',  img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/plain-icon_copy.png',   img_photo: true, next: 'Q_metal' }
        ]
      },
      Q_conch_stone_type: {
        key: 'stone_type', question: 'Pick your stone', cols: 3,
        helix: { title: 'Diamond, CZ, or colored', body: "Same options as other ear piercings." },
        options: [
          { value: 'diamond',  label: 'Diamond',        img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/diamond-round-icon-opt.webp', img_photo: true, writes: { ornament_type: 'gemstone', stone_type: 'diamond'  }, next: 'Q_metal' },
          { value: 'cz',       label: 'Cubic Zirconia',  img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/cz-icon_copy.png',        img_photo: true, writes: { ornament_type: 'gemstone', stone_type: 'cz'       }, next: 'Q_metal' },
          { value: 'precious', label: 'Colored gem',     img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/gemstone-icon_copy.png',  img_photo: true, writes: { ornament_type: 'gemstone', stone_type: 'gemstone' }, next: 'Q_metal' }
        ]
      },
      Q_conch_gauge: {
        key: 'gauge', question: 'Pick your gauge', cols: 2,
        helix: {
          title: 'Conch gauge',
          body: "16G (1.2mm) is standard for outer conch. Inner conch is sometimes done at 14G (1.6mm) for larger statement jewelry."
        },
        options: [
          { value: '16G', label: '16G', desc: '1.2mm — standard', next: 'Q_conch_dimension' },
          { value: '14G', label: '14G', desc: '1.6mm — statement', next: 'Q_conch_dimension' }
        ]
      },
      Q_conch_dimension: {
        key: 'dimension', question: 'Pick your size', cols: 3,
        helix: { title: 'Post length or diameter', body: "Conch pieces need longer posts than lobe or helix." },
        options: [
          { value: '5/16', label: '5/16"', desc: '(8mm)',  next: 'final' },
          { value: '3/8',  label: '3/8"',  desc: '(10mm)', next: 'final' },
          { value: '1/2',  label: '1/2"',  desc: '(13mm)', next: 'final' }
        ]
      },

      // ── CURVED-BAR branch (rook / daith / snug) ─────────────────────
      Q_curved_form: {
        key: 'form', question: 'Curved bar or hoop?', cols: 2,
        helix: {
          title: 'Curved-piercing forms',
          body: "Rook, daith, and snug can wear a small curved barbell, or a hoop (hinged clicker / seamless captive ring)."
        },
        // Values map to the emitter's 3-bucket product_form field (stud/hoop/
        // charm — see quiz-catalog-emitter.liquid). Clicker and seamless ring
        // were previously shown as two separate tiles both writing 'hoop' —
        // the catalog has no attribute distinguishing them (curved-barbell
        // products use a "Barbell Length" option -> 'stud'; clickers and seam
        // rings both use "Hoop Diameter" -> 'hoop'), so per Ximena they're
        // now one "Hoop" tile, matching every other category's stud-or-hoop
        // pattern instead of exposing a distinction the data can't back up.
        options: [
          { value: 'curved_bar', label: 'Curved bar', img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/curved-barbell-icon.png', writes: { form: 'stud' }, next: 'Q_curved_ornament' },
          { value: 'hoop', label: 'Hoop',        img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/style-hoop-icon.png', next: 'Q_curved_ornament' }
        ]
      },
      Q_curved_ornament: {
        key: 'ornament_type', question: 'Stone type', cols: 3,
        helix: {
          title: 'Gemstone, charm, or plain',
          body: "A gemstone is a faceted sparkling stone — diamond, CZ, or colored gem. A charm is a small shaped ornament — star, moon, heart, flower — with no faceted stone. Plain is solid gold, no ornament."
        },
        options: [
          { value: 'gemstone', label: 'Gemstone',          img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/gemstone-icon.png?v=2', next: 'Q_curved_stone_type' },
          { value: 'charm',    label: 'Charm',              img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/charm-icon.png',        next: 'Q_metal' },
          { value: 'plain',    label: 'Plain (no stone)',  img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/plain-icon_copy.png',   img_photo: true, next: 'Q_metal' }
        ]
      },
      Q_curved_stone_type: {
        key: 'stone_type', question: 'Pick your stone', cols: 3,
        helix: { title: 'Diamond, CZ, or colored', body: "Same options." },
        options: [
          { value: 'diamond',  label: 'Diamond',        img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/diamond-round-icon-opt.webp', img_photo: true, writes: { ornament_type: 'gemstone', stone_type: 'diamond'  }, next: 'Q_metal' },
          { value: 'cz',       label: 'Cubic Zirconia',  img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/cz-icon_copy.png',        img_photo: true, writes: { ornament_type: 'gemstone', stone_type: 'cz'       }, next: 'Q_metal' },
          { value: 'precious', label: 'Colored gem',     img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/gemstone-icon_copy.png',  img_photo: true, writes: { ornament_type: 'gemstone', stone_type: 'gemstone' }, next: 'Q_metal' }
        ]
      },
      Q_curved_gauge: {
        key: 'gauge', question: 'Pick your gauge', cols: 2,
        helix: {
          title: 'Curved-piercing gauge',
          body: "16G (1.2mm) is standard for rook, daith, and snug. Some daith piercings are done at 18G."
        },
        options: [
          { value: '18G', label: '18G', desc: '1.0mm — slimmer',  next: 'Q_curved_dimension' },
          { value: '16G', label: '16G', desc: '1.2mm — standard', next: 'Q_curved_dimension' }
        ]
      },
      Q_curved_dimension: {
        key: 'dimension', question: 'Pick your size', cols: 3,
        helix: { title: 'Size', body: "For a curved bar, this is the length. For a ring or clicker, this is the diameter." },
        options: [
          { value: '1/4',  label: '1/4"',  desc: '(6mm)',  next: 'final' },
          { value: '5/16', label: '5/16"', desc: '(8mm)',  next: 'final' },
          { value: '3/8',  label: '3/8"',  desc: '(10mm)', next: 'final' }
        ]
      },

      // ── INDUSTRIAL branch (straight barbell only) ───────────────────
      Q_ind_ornament: {
        key: 'ornament_type', question: 'Ornament', cols: 3,
        helix: {
          title: 'Industrial jewelry',
          body: "Industrial piercings span two piercings and wear a single straight barbell. Pick your ornament — the accent can sit at the ends or along the middle of the bar."
        },
        // form writes 'stud', not 'straight_barbell' — the catalog emitter
        // only ever tags products stud/hoop/charm (see quiz-catalog-emitter
        // .liquid), and industrial barbells use a "Barbell Length" option
        // (no Diameter/Charm Size), so they fall to the emitter's 'stud'
        // default. 'straight_barbell' never matched any product, and since
        // form is structural for this category (inherited from 'ear'), every
        // industrial completion was forced into the bestseller fallback.
        options: [
          { value: 'plain',    label: 'Plain (no stone)',    img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/plain-icon_copy.png',     img_photo: true, writes: { form: 'stud', ornament_type: 'plain'    }, next: 'Q_metal' },
          { value: 'diamond',  label: 'Diamond',  img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/diamond-round-icon-opt.webp', img_photo: true, writes: { form: 'stud', ornament_type: 'gemstone', stone_type: 'diamond' }, next: 'Q_metal' },
          { value: 'cz',       label: 'CZ',       img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/cz-icon_copy.png',        img_photo: true, writes: { form: 'stud', ornament_type: 'gemstone', stone_type: 'cz'      }, next: 'Q_metal' }
        ]
      },
      Q_ind_dimension: {
        key: 'dimension', question: 'Pick your length', cols: 3,
        helix: {
          title: 'Industrial length',
          body: "Industrial barbells are long — they span two piercings. Standard lengths are 1 1/4\", 1 3/8\", and 1 1/2\". Longer is more common."
        },
        options: [
          { value: '1 1/4', label: '1 1/4"', desc: '(32mm)', writes: { gauge: '14G' }, next: 'final' },
          { value: '1 3/8', label: '1 3/8"', desc: '(35mm)', writes: { gauge: '14G' }, next: 'final' },
          { value: '1 1/2', label: '1 1/2"', desc: '(38mm)', writes: { gauge: '14G' }, next: 'final' }
        ]
      }
    },
    intro_helix: INTRO_HELIX
  });

  // ═══════════════════════════════════════════════════════════════════
  //   NIPPLE
  // ═══════════════════════════════════════════════════════════════════

  var nippleRings = makeCategory({
    id: 'nipple',
    label: 'Nipple Rings',
    intro: "A few quick questions and we'll match you to your ideal nipple jewelry.",
    catalog_url: '/collections/nipple-piercing-jewelry?view=quiz-catalog',
    initial: 'Q_nipple_form',
    structural_keys: ['form', 'ornament_type'],
    defaults: { gauge: '14G', dimension: '1/2' },
    questions: {
      // Metal moves right after ornament/stone (Ximena, 2026-08-12).
      Q_metal: metalQ('Q_nipple_gauge'),
      Q_nipple_form: {
        key: 'form', question: 'Straight bar or hoop?', cols: 2,
        helix: {
          title: 'Bar vs hoop',
          body: "Straight barbells are the most common nipple jewelry — comfortable for side-sleepers. Hoops (captive or seamless) are more decorative but sit differently against clothing."
        },
        // 'stud', not 'straight_barbell' — the emitter only ever tags
        // products stud/hoop/charm, and nipple straight barbells use a
        // "Barbell Length" option (no Diameter), so they default to 'stud'.
        // 'straight_barbell' never matched a single product — since form is
        // structural here, "Straight bar" (the more common nipple choice per
        // the copy above) always returned 0 results and forced the fallback
        // state, even though it's live-hidden safety-valve-shown (not
        // visibly missing) because "Hoop" also survives.
        options: [
          { value: 'stud', label: 'Straight Barbell', img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/style-barbell-icon-v2.png', next: 'Q_nipple_ornament' },
          { value: 'hoop', label: 'Hoop',         img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/style-hoop-icon.png', next: 'Q_nipple_ornament' }
        ]
      },
      Q_nipple_ornament: {
        key: 'ornament_type', question: 'Gemstone or plain?', cols: 3,
        helix: {
          title: 'Gemstone or plain',
          body: "A gemstone is a faceted sparkling stone — diamond, CZ, or colored gem. Plain is solid-gold balls, no stone."
        },
        // Question/helix text intentionally omits "charm" (Ximena, 2026-08-11)
        // even though the charm option is still configured below — the
        // shields/stirrups products it needs haven't landed yet, so showing
        // "charm" in the copy while no charm tile renders read as a bug.
        // Re-add "charm" to the text once those products are live and
        // tagged (the tile itself will already be there, live-hidden).
        //
        // Gemstone leads to a Diamond / CZ / Colored drill-down (Ximena,
        // 2026-08-11) instead of pre-merging Diamond+CZ into one tile —
        // matches the nose stud flow (Q_stud_ornament -> Q_stud_stone_type).
        // Charm is here for the shields/stirrups landing this week — until
        // those (charm-classified) products exist the option auto-hides
        // (refreshOptions live-hides structural options with 0 matches), then
        // appears on its own.
        options: [
          { value: 'gemstone', label: 'Gemstone', img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/gemstone-icon.png?v=2', writes: { ornament_type: 'gemstone' }, next: 'Q_nipple_stone_type' },
          { value: 'charm',    label: 'Charm',     img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/charm-icon.png',                       writes: { ornament_type: 'charm' }, next: 'Q_metal' },
          { value: 'plain',    label: 'Plain (no stone)',  img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/plain-icon_copy.png', img_photo: true, writes: { ornament_type: 'plain' },  next: 'Q_metal' }
        ]
      },
      Q_nipple_stone_type: {
        key: 'stone_type', question: 'Diamond, CZ, or colored?', cols: 3,
        helix: {
          title: 'Diamond, CZ, or colored',
          body: "Diamonds are real, lab-graded VS-quality. Cubic zirconia is synthetic — it looks like a diamond and costs much less. A great starter option. Colored gemstones (sapphire, opal, and other colors) add personality."
        },
        options: [
          { value: 'diamond',  label: 'Diamond',           desc: 'Real, VS-quality',        img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/diamond-round-icon-opt.webp',   img_photo: true, next: 'Q_metal' },
          { value: 'cz',       label: 'Cubic Zirconia',    desc: 'Synthetic, budget-friendly', img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/cz-icon_copy.png',        img_photo: true, next: 'Q_metal' },
          { value: 'gemstone', label: 'Colored Gemstones', desc: 'Sapphire, opal & more',    img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/gemstone-icon_copy.png',  img_photo: true, next: 'Q_metal' }
        ]
      },
      Q_nipple_gauge: {
        key: 'gauge', question: 'Pick your gauge', cols: 3,
        helix: {
          title: 'Nipple gauge',
          body: "14G (1.6mm) is the universal nipple standard. 12G (2.0mm) is thicker — used by some for a more substantial feel. 16G (1.2mm) is slimmer, less common. Match what you currently wear."
        },
        options: [
          { value: '16G', label: '16G', desc: '1.2mm — slimmer',  next: 'Q_nipple_dimension' },
          { value: '14G', label: '14G', desc: '1.6mm — standard', next: 'Q_nipple_dimension' },
          { value: '12G', label: '12G', desc: '2.0mm — thicker',  next: 'Q_nipple_dimension' }
        ]
      },
      Q_nipple_dimension: {
        key: 'dimension', question: 'Pick your size', cols: 3,
        helix: {
          title: 'Length or diameter',
          body: "For bars, this is length. For hoops, diameter. Nipple jewelry sizing is anatomical — match what you currently wear."
        },
        options: [
          { value: '1/2',  label: '1/2"',  desc: '(13mm)', next: 'final' },
          { value: '5/8',  label: '5/8"',  desc: '(16mm)', next: 'final' },
          { value: '3/4',  label: '3/4"',  desc: '(19mm)', next: 'final' }
        ]
      }
    },
    intro_helix: INTRO_HELIX
  });

  // ═══════════════════════════════════════════════════════════════════
  //   LIP FAMILY — labret / side labret / monroe-medusa / madonna / ashley
  // ═══════════════════════════════════════════════════════════════════

  var lipRings = makeCategory({
    id: 'lip',
    label: 'Lip Rings',
    intro: "Lip & mouth jewelry — flat-back studs and rings, sized to fit.",
    catalog_url: '/collections/lip-rings?view=quiz-catalog',
    initial: 'Q_lip_form',
    structural_keys: ['form', 'ornament_type'],
    defaults: { gauge: '16G', dimension: '5/16' },
    questions: {
      // Metal moves right after ornament/stone (Ximena, 2026-08-12).
      Q_metal: metalQ('Q_lip_gauge'),
      // Lip opens with jewelry TYPE (stud vs ring), not placement. Lip products
      // are overwhelmingly flat-back labret studs that work across placements
      // (labret / side labret / medusa / madonna-monroe / ashley), so the
      // placement question narrowed almost nothing while adding friction; the
      // fit differences it implied are folded into the size step below.
      Q_lip_form: {
        key: 'form', question: 'Flat-back stud or ring?', cols: 2,
        helix: {
          title: 'Stud vs ring',
          body: "Most lip and mouth piercings wear flat-back labret studs — flush inside the mouth, ornament outside. Outer lip and some labret can also wear a ring."
        },
        options: [
          // NOTE: do NOT write retention:'flatback' here. No lip product carries
          // a bare 'flatback' style value (the catalog emits threaded /
          // threadless / threadless-push-pin / flatback-threadless /
          // flatback-threaded), so that filter matched zero products and forced
          // the soft-fallback to strip stone_type (surfacing CZ/colored under a
          // Diamond pick). form:'stud' already scopes to studs.
          { value: 'stud', label: 'Flat-back stud', img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/backing-icon-flatback.png', writes: { form: 'stud' }, next: 'Q_lip_ornament' },
          { value: 'hoop',   label: 'Ring',           img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/style-hoop-icon.png',                                    next: 'Q_lip_ornament' }
        ]
      },
      Q_lip_ornament: {
        key: 'ornament_type', question: 'Top style', cols: 3,
        helix: {
          title: 'Gemstone, charm, or plain',
          body: "A gemstone is a faceted sparkling stone — diamond, CZ, or colored gem. A charm is a small shaped ornament — star, moon, heart, flower — with no faceted stone. Plain is solid gold, no ornament."
        },
        options: [
          { value: 'gemstone', label: 'Gemstone',          img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/gemstone-icon.png?v=2', next: 'Q_lip_stone_type' },
          { value: 'charm',    label: 'Charm',              img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/charm-icon.png',        next: 'Q_metal' },
          { value: 'plain',    label: 'Plain (no stone)',  img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/plain-icon_copy.png',   img_photo: true, next: 'Q_metal' }
        ]
      },
      Q_lip_stone_type: {
        key: 'stone_type', question: 'Pick your stone', cols: 3,
        helix: {
          title: 'Diamond, CZ, or colored',
          body: "Diamonds are real, lab-graded VS-quality. Cubic zirconia looks like a diamond and costs much less — a great starter option. Colored precious / semi-precious stones add personality."
        },
        options: [
          { value: 'diamond',  label: 'Diamond',        img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/diamond-round-icon-opt.webp', img_photo: true, writes: { ornament_type: 'gemstone', stone_type: 'diamond'  }, next: 'Q_metal' },
          { value: 'cz',       label: 'Cubic Zirconia',  img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/cz-icon_copy.png',        img_photo: true, writes: { ornament_type: 'gemstone', stone_type: 'cz'       }, next: 'Q_metal' },
          { value: 'precious', label: 'Colored gem',     img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/gemstone-icon_copy.png',  img_photo: true, writes: { ornament_type: 'gemstone', stone_type: 'gemstone' }, next: 'Q_metal' }
        ]
      },
      Q_lip_gauge: {
        key: 'gauge', question: 'Pick your gauge', cols: 3,
        helix: {
          title: 'Lip gauge',
          body: "16G (1.2mm) is standard for labret, side labret, Medusa, and Ashley. Madonna / Monroe is often 18G. Match your current piercing."
        },
        options: [
          { value: '18G', label: '18G', desc: '1.0mm', next: 'Q_lip_dimension' },
          { value: '16G', label: '16G', desc: '1.2mm — standard', next: 'Q_lip_dimension' },
          { value: '14G', label: '14G', desc: '1.6mm', next: 'Q_lip_dimension' }
        ]
      },
      Q_lip_dimension: {
        key: 'dimension', question: 'Pick your size', cols: 3,
        helix: {
          title: 'Length or diameter',
          body: "For a flat-back stud this is the labret post length. 5/16\" (8mm) fits most <b>healed</b> labret, side labret, Medusa, Monroe, and Ashley piercings snugly. Size up to 3/8\" (10mm) or 7/16\" (11mm) for a <b>fresh</b> piercing (room to swell), a fuller lip, or a lower labret. For a ring, this is the diameter."
        },
        options: [
          { value: '5/16', label: '5/16"', desc: '(8mm)',  next: 'final' },
          { value: '3/8',  label: '3/8"',  desc: '(10mm)', next: 'final' },
          { value: '7/16', label: '7/16"', desc: '(11mm)', next: 'final' }
        ]
      }
    },
    intro_helix: INTRO_HELIX
  });

  // ═══════════════════════════════════════════════════════════════════
  //   EYEBROW — small curved barbell family
  // ═══════════════════════════════════════════════════════════════════

  var eyebrowRings = makeCategory({
    id: 'eyebrow',
    label: 'Eyebrow Rings',
    intro: "A few quick questions and we'll match you to your ideal eyebrow ring.",
    catalog_url: '/collections/eyebrow-rings?view=quiz-catalog',
    initial: 'Q_eb_form',
    structural_keys: ['form', 'ornament_type'],
    defaults: { gauge: '16G', dimension: '5/16' },
    questions: {
      // Metal moves right after ornament/stone (Ximena, 2026-08-12).
      Q_metal: metalQ('Q_eb_gauge'),
      // Form FIRST (Paul, 2026-08-10): asking ornament before form let "Plain"
      // match plain RINGS (seam rings / clickers) as well as plain-ball curved
      // barbells, so seam rings showed under "Plain balls". Splitting shape
      // first — curved barbell (form=stud) vs ring (form=hoop) — keeps plain
      // barbells and plain rings in their own lanes.
      Q_eb_form: {
        key: 'form', question: 'Curved barbell or ring?', cols: 2,
        helix: {
          title: 'Barbell or ring',
          body: "An eyebrow curved barbell is a short banana-shaped bar with a bead on each end. A ring is a hoop — seam ring, clicker, or circular barbell — that curves through the piercing."
        },
        options: [
          { value: 'eyebrow_curved', label: 'Curved barbell', img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/curved-barbell-icon.png', writes: { form: 'stud' }, next: 'Q_eb_ornament' },
          { value: 'hoop', label: 'Ring',           img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/style-hoop-icon.png', writes: { form: 'hoop' }, next: 'Q_eb_ornament' }
        ]
      },
      Q_eb_ornament: {
        key: 'stone_type', question: 'Diamond/CZ, colored gem, or plain?', cols: 3,
        helix: {
          title: 'Stone or plain',
          body: "Diamond / CZ — a clear sparkling stone (real lab-graded diamond, or CZ for the same look for less). Colored gemstones — emerald, ruby, sapphire, and other colors. Plain — solid gold, no stone."
        },
        options: [
          { value: 'diamond_cz', label: 'Diamond / CZ',       img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/diamond-round-icon-opt.webp', img_photo: true, writes: { ornament_type: 'gemstone', stone_type: 'diamond_cz' }, next: 'Q_metal' },
          { value: 'gemstone',   label: 'Colored gemstones',  img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/gemstone-icon_copy.png',  img_photo: true, writes: { ornament_type: 'gemstone', stone_type: 'gemstone' },   next: 'Q_metal' },
          { value: 'plain',      label: 'Plain (no stone)',   img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/plain-icon_copy.png',     img_photo: true, writes: { ornament_type: 'plain' },                              next: 'Q_metal' }
        ]
      },
      Q_eb_gauge: {
        key: 'gauge', question: 'Pick your gauge', cols: 2,
        helix: {
          title: 'Eyebrow gauge',
          body: "16G (1.2mm) is the standard for eyebrow piercings. 14G (1.6mm) is thicker, sometimes used for a more substantial look."
        },
        options: [
          { value: '16G', label: '16G', desc: '1.2mm — standard', next: 'Q_eb_dimension' },
          { value: '14G', label: '14G', desc: '1.6mm — thicker',  next: 'Q_eb_dimension' }
        ]
      },
      Q_eb_dimension: {
        key: 'dimension', question: 'Pick your length', cols: 3,
        helix: {
          title: 'Barbell length',
          body: "Eyebrow curved barbells are short. 1/4\" (6mm) is snug, 5/16\" (8mm) is standard, 3/8\" (10mm) is longer."
        },
        options: [
          { value: '1/4',  label: '1/4"',  desc: '(6mm)',  next: 'final' },
          { value: '5/16', label: '5/16"', desc: '(8mm)',  next: 'final' },
          { value: '3/8',  label: '3/8"',  desc: '(10mm)', next: 'final' }
        ]
      }
    },
    intro_helix: INTRO_HELIX
  });

  // ═══════════════════════════════════════════════════════════════════
  //   SEPTUM
  // ═══════════════════════════════════════════════════════════════════

  var septumRings = makeCategory({
    id: 'septum',
    label: 'Septum Rings',
    intro: "A few quick questions and we'll match you to your ideal septum piece.",
    catalog_url: '/collections/septum-rings?view=quiz-catalog',
    initial: 'Q_sep_form',
    structural_keys: ['form', 'ornament_type'],
    defaults: { gauge: '16G', dimension: '5/16' },
    questions: {
      // Metal moves right after ornament/stone (Ximena, 2026-08-12).
      Q_metal: metalQ('Q_sep_gauge'),
      Q_sep_form: {
        key: 'form', question: 'Hoop or horseshoe?', cols: 2,
        helix: {
          title: 'Septum forms',
          body: "A hoop (clicker or seamless ring) is a closed circle — most traditional. A horseshoe (circular barbell) has a small gap with a ball on each end, and can flip up inside the nose to hide the piercing."
        },
        // Both map to the emitter's 'hoop' bucket — verified against real
        // catalog data (clickers, seam rings, AND circular barbells/horseshoes
        // all use a "Hoop Diameter" option; see quiz-catalog-emitter.liquid).
        // The catalog has no attribute distinguishing hoop shapes for septum
        // jewelry, so Clicker/Seamless ring were merged into one "Hoop" tile
        // (Ximena, 2026-08-12) rather than offering a 3-way split that
        // resolves to the same product pool regardless of pick. Previously
        // these wrote non-catalog values ('clicker'/'seamless_ring'/
        // 'curved_barbell') that matched zero products regardless of choice,
        // forcing every septum shopper into the bestseller-fallback state.
        options: [
          { value: 'hoop', label: 'Hoop',      img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/style-hoop-icon.png',      next: 'Q_sep_ornament' },
          { value: 'hoop', label: 'Horseshoe', img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/circular-barbell-icon.png', next: 'Q_sep_ornament' }
        ]
      },
      Q_sep_ornament: {
        key: 'ornament_type', question: 'Stone type', cols: 4,
        helix: { title: 'Stone or plain', body: "Same options." },
        options: [
          { value: 'diamond',  label: 'Diamond',          img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/diamond-round-icon-opt.webp', img_photo: true, writes: { ornament_type: 'gemstone', stone_type: 'diamond'  }, next: 'Q_metal' },
          { value: 'cz',       label: 'Cubic Zirconia',   img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/cz-icon_copy.png',        img_photo: true, writes: { ornament_type: 'gemstone', stone_type: 'cz'       }, next: 'Q_metal' },
          { value: 'precious', label: 'Colored gem',      img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/gemstone-icon_copy.png',  img_photo: true, writes: { ornament_type: 'gemstone', stone_type: 'gemstone' }, next: 'Q_metal' },
          { value: 'plain',    label: 'Plain (no stone)', img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/plain-icon_copy.png',     img_photo: true, writes: { ornament_type: 'plain' },                            next: 'Q_metal' }
        ]
      },
      Q_sep_gauge: {
        key: 'gauge', question: 'Pick your gauge', cols: 2,
        helix: {
          title: 'Septum gauge',
          body: "16G (1.2mm) is standard for septum. Some older piercings are 14G (1.6mm). Match your current piece."
        },
        options: [
          { value: '16G', label: '16G', desc: '1.2mm — standard', next: 'Q_sep_dimension' },
          { value: '14G', label: '14G', desc: '1.6mm — thicker',  next: 'Q_sep_dimension' }
        ]
      },
      Q_sep_dimension: {
        key: 'dimension', question: 'Pick your diameter', cols: 3,
        helix: {
          title: 'Ring diameter',
          body: "Small (5/16\" / 8mm) is snug. Standard (3/8\" / 10mm) is the most common. Large (7/16\" / 11mm) is a fuller statement."
        },
        options: [
          { value: '5/16', label: 'Small',    desc: '5/16" (8mm)',  next: 'final' },
          { value: '3/8',  label: 'Standard', desc: '3/8" (10mm)',  next: 'final' },
          { value: '7/16', label: 'Large',    desc: '7/16" (11mm)', next: 'final' }
        ]
      }
    },
    intro_helix: INTRO_HELIX
  });

  // ═══════════════════════════════════════════════════════════════════
  //   TONGUE — straight barbell only
  // ═══════════════════════════════════════════════════════════════════

  var tongueRings = makeCategory({
    id: 'tongue',
    label: 'Tongue Rings',
    intro: "A few quick questions and we'll match you to your ideal tongue ring.",
    catalog_url: '/collections/gold-tongue-rings?view=quiz-catalog',
    initial: 'Q_tng_ornament',
    // form implicit: straight_barbell only
    structural_keys: ['ornament_type'],
    defaults: { gauge: '14G', dimension: '5/8' },
    questions: {
      // Metal moves right after ornament (Ximena, 2026-08-12).
      Q_metal: metalQ('Q_tng_gauge'),
      Q_tng_ornament: {
        key: 'ornament_type', question: 'Plain or gemstone?', cols: 2,
        helix: {
          title: 'Ornament',
          body: "Tongue barbells wear a plain solid-gold ball or a jeweled end — diamond, CZ, or a colored gemstone."
        },
        // form writes 'stud' (tongue barbells use a Barbell Length option, no
        // Diameter, so the emitter tags them 'stud'). No "Dangle" — tongue
        // barbells don't come as dangles. "Gemstone" is a SINGLE bucket (no
        // diamond/CZ/colored drill-down) writing only ornament_type:'gemstone',
        // so every jeweled tongue ring — diamond, sapphire, colored — shows
        // under it (the ornament_type=gemstone predicate matches all three
        // stone types). (Paul, 2026-08-10)
        options: [
          { value: 'plain',    label: 'Plain (no stone)', img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/plain-icon_copy.png',    img_photo: true, writes: { form: 'stud', ornament_type: 'plain' },    next: 'Q_metal' },
          { value: 'gemstone', label: 'Gemstone',   img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/gemstone-icon.png?v=2',                  writes: { form: 'stud', ornament_type: 'gemstone' }, next: 'Q_metal' }
        ]
      },
      Q_tng_gauge: {
        key: 'gauge', question: 'Pick your gauge', cols: 2,
        helix: {
          title: 'Tongue gauge',
          body: "14G (1.6mm) is the universal tongue-piercing standard. 12G (2.0mm) is thicker, used for stretched or larger jewelry."
        },
        options: [
          { value: '14G', label: '14G', desc: '1.6mm — standard', next: 'Q_tng_dimension' },
          { value: '12G', label: '12G', desc: '2.0mm — thicker',  next: 'Q_tng_dimension' }
        ]
      },
      Q_tng_dimension: {
        key: 'dimension', question: 'Pick your length', cols: 3,
        helix: {
          title: 'Barbell length',
          body: "New tongue piercings use a longer barbell (7/8\") to allow for swelling. Once healed, most people wear 5/8\" or 3/4\"."
        },
        options: [
          { value: '5/8', label: '5/8"', desc: '(16mm)',  next: 'final' },
          { value: '3/4', label: '3/4"', desc: '(19mm)',  next: 'final' },
          { value: '7/8', label: '7/8"', desc: '(22mm — new)', next: 'final' }
        ]
      }
    },
    intro_helix: INTRO_HELIX
  });

  // ═══════════════════════════════════════════════════════════════════
  //   ALL-CATEGORY ROUTER — homepage entry point
  //   Not a real filter — categorySwitch on each option (handled by
  //   guided-shopping.js onAnswer) hands off to the picked category quiz.
  // ═══════════════════════════════════════════════════════════════════

  var allCategories = {
    id: 'all',
    label: 'All Piercings',
    intro: "Tell us what you're shopping for and we'll guide you to the perfect piece.",
    catalogUrl: null,
    catalog_url: null,
    initial: 'Q_type',
    structuralKeys: {},
    structural_keys: [],
    defaults: {},
    questions: {
      Q_type: {
        key: 'piercing_category', question: 'What type of piercing?', cols: 4,
        helix: {
          title: "Let's find your perfect piece",
          body: "Pick your piercing type and I'll take you through a short quiz — usually 3–5 questions — to match you to the right piece."
        },
        options: [
          { value: 'nose',    label: 'Nose Ring',    img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/nose-piercing-icon.jpg',     img_photo: true, categorySwitch: 'nose'    },
          { value: 'ear',     label: 'Ear',          img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/ear-piercings-icon.webp',    img_photo: true, categorySwitch: 'ear'     },
          { value: 'belly',   label: 'Belly Button', img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/belly-piercing-icon.webp',   img_photo: true, categorySwitch: 'belly'   },
          { value: 'lip',     label: 'Lip',          img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/lip-piercings-icon.webp',    img_photo: true, categorySwitch: 'lip'     },
          { value: 'tongue',  label: 'Tongue',       img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/tongue-piercing-icon.webp',  img_photo: true, categorySwitch: 'tongue'  },
          { value: 'eyebrow', label: 'Eyebrow',      img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/eyebrow-piercing-icon.webp', img_photo: true, categorySwitch: 'eyebrow' },
          { value: 'nipple',  label: 'Nipple',       img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/nipple-piercings-icon.webp', img_photo: true, categorySwitch: 'nipple'  },
          { value: 'septum',  label: 'Septum',       img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/septum-piercing-icon.webp',  img_photo: true, categorySwitch: 'septum'  }
        ]
      }
    },
    filter: function () { return []; },
    strictCount: function () { return 999; },
    intro_helix: INTRO_HELIX
  };

  // ═══════════════════════════════════════════════════════════════════
  //   DYNAMIC QUESTION BUILDER — for collections that use the
  //   custom.collection_filters metafield to declare the quiz shape.
  //   Kept for compatibility with Tom's dynamic-metafield-driven flow.
  //   Output is adapted to emit schema-compliant answers.
  // ═══════════════════════════════════════════════════════════════════

  function dynUniq(catalog, field) {
    var seen = {}, out = [];
    for (var i = 0; i < catalog.length; i++) {
      var v = catalog[i][field];
      if (v && !seen[v]) { seen[v] = 1; out.push(v); }
    }
    return out;
  }
  function dynUniqArr(catalog, field) {
    var seen = {}, out = [];
    for (var i = 0; i < catalog.length; i++) {
      var arr = catalog[i][field];
      if (!Array.isArray(arr)) continue;
      for (var j = 0; j < arr.length; j++) {
        var v = arr[j];
        if (v && !seen[v]) { seen[v] = 1; out.push(v); }
      }
    }
    return out;
  }

  // Builders keyed by the filter name in the collection metafield.
  var DYN_BUILDERS = {
    form: function (catalog) {
      var forms = dynUniq(catalog, 'form');
      return {
        key: 'form', question: 'Pick your style',
        options: forms.map(function (f) { return { value: f, label: f, next: '' }; }),
        cols: forms.length
      };
    },
    ornament_type: function () {
      return {
        key: 'ornament_type', question: 'Stone type',
        options: [
          { value: 'diamond',  label: 'Diamond',          img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/diamond-round-icon-opt.webp', img_photo: true, writes: { ornament_type: 'gemstone', stone_type: 'diamond'  }, next: '' },
          { value: 'cz',       label: 'Cubic Zirconia',   img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/cz-icon_copy.png',        img_photo: true, writes: { ornament_type: 'gemstone', stone_type: 'cz'       }, next: '' },
          { value: 'precious', label: 'Colored gem',      img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/gemstone-icon_copy.png',  img_photo: true, writes: { ornament_type: 'gemstone', stone_type: 'gemstone' }, next: '' },
          { value: 'plain',    label: 'Plain (no stone)', img: 'https://cdn.shopify.com/s/files/1/2395/3423/files/plain-icon_copy.png',     img_photo: true, writes: { ornament_type: 'plain' },                            next: '' }
        ],
        cols: 4
      };
    },
    metal: function (catalog) {
      var metals = dynUniqArr(catalog, 'metals');
      return {
        key: 'metal', question: 'Metal color',
        options: metals.map(function (m) { return { value: m.toLowerCase(), label: m, next: '' }; }),
        cols: Math.min(metals.length, 4)
      };
    }
  };

  function dynGaugeQuestion(catalog) {
    var raw = dynUniqArr(catalog, 'gauges').map(normalizeGauge).filter(Boolean);
    var uniq = {}; var g = [];
    raw.forEach(function (v) { if (!uniq[v]) { uniq[v] = 1; g.push(v); } });
    // Sort thin-to-thick (higher gauge number = thinner post):
    // 22G, 20G, 18G, 16G, 14G, 12G, 10G, 8G
    g.sort(function (a, b) { return parseInt(b, 10) - parseInt(a, 10); });
    var descs = { '22G': '0.6mm', '20G': '0.8mm', '18G': '1.0mm', '16G': '1.2mm', '14G': '1.6mm', '12G': '2.0mm', '10G': '2.4mm', '8G': '3.2mm' };
    return {
      key: 'gauge', question: 'Pick your gauge',
      options: g.map(function (x) { return { value: x, label: x, desc: descs[x] || '', next: '' }; }),
      cols: Math.min(g.length, 3)
    };
  }
  function dynDimensionQuestion(catalog) {
    var pool = [].concat(dynUniqArr(catalog, 'post_lengths')).concat(dynUniqArr(catalog, 'hoop_diameters'));
    var uniq = {}; var out = [];
    pool.forEach(function (v) {
      var n = normalizeDim(v);
      if (n && !uniq[n]) { uniq[n] = 1; out.push(n); }
    });
    return {
      key: 'dimension', question: 'Pick your size',
      options: out.map(function (d) { return { value: d, label: d + '"', next: '' }; }),
      cols: Math.min(out.length, 4)
    };
  }

  // Called by guided-shopping.js when a collection's meta.filters is non-empty.
  // Signature matches applyDynamicQuestions() in the runtime:
  //   buildDynamicCategory(filters, exclude, products, parentCategory)
  // Returns a category-like object the engine can drive.
  function buildDynamicCategory(filters, exclude, catalog, parentCategory) {
    filters = filters || [];
    catalog = catalog || [];
    if (!filters.length) return null;

    var questions = {}; var sequence = [];
    filters.forEach(function (name) {
      var builder = DYN_BUILDERS[name.toLowerCase().trim()];
      if (!builder) return;
      var q = builder(catalog, {});
      if (!q || !q.options.length) return;
      var id = 'D_' + name;
      questions[id] = q;
      sequence.push(id);
    });
    var g = dynGaugeQuestion(catalog);
    if (g.options.length) { questions['D_gauge'] = g; sequence.push('D_gauge'); }
    var d = dynDimensionQuestion(catalog);
    if (d.options.length) { questions['D_dimension'] = d; sequence.push('D_dimension'); }

    // Wire the sequence
    for (var i = 0; i < sequence.length; i++) {
      var next = i + 1 < sequence.length ? sequence[i + 1] : 'final';
      questions[sequence[i]].options.forEach(function (o) { o.next = next; });
    }

    var usedKeys = sequence.map(function (id) { return questions[id].key; });
    var structural = ['form', 'ornament_type'].filter(function (k) { return usedKeys.indexOf(k) !== -1; });

    return makeCategory({
      id:              (parentCategory && parentCategory.id) || 'dynamic',
      label:           (parentCategory && parentCategory.label) || 'Quick match',
      intro:           (parentCategory && parentCategory.intro) || "A few quick questions and we'll narrow down the collection.",
      catalog_url:     parentCategory && parentCategory.catalog_url,
      initial:         sequence[0],
      structural_keys: structural,
      questions:       questions,
      intro_helix:     (parentCategory && parentCategory.intro_helix) || INTRO_HELIX
    });
  }

  // ═══════════════════════════════════════════════════════════════════
  //   REGISTRATION
  // ═══════════════════════════════════════════════════════════════════

  win.FT_QUIZ_CONFIG = {
    nose:    noseRings,
    belly:   bellyRings,
    ear:     earPiercings,
    lip:     lipRings,
    nipple:  nippleRings,
    eyebrow: eyebrowRings,
    septum:  septumRings,
    tongue:  tongueRings,
    all:     allCategories
  };

  // Expose the dynamic builder for guided-shopping.js
  win.FT_QUIZ_BUILD_DYNAMIC = buildDynamicCategory;
})(window);
